import { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Bold, Italic, Image, Type, Eye, EyeOff, Upload, Link, Download, Shapes, Heading, List, Table2, MessageSquareQuote, X } from 'lucide-react';
import QuestionVisual from '../quiz/QuestionVisual';
import api from '../../services/api.js';
import { handleSmartPaste } from './sanitizePaste';

const RENDERER_OPTIONS = [
    { value: 'card_sequence', label: 'Cartes à jouer' },
    { value: 'domino_sequence', label: 'Dominos' },
    { value: 'series', label: 'Série (alpha/num)' },
    { value: 'matrix', label: 'Matrice' },
    { value: 'planning', label: 'Planning' },
    { value: 'compass', label: 'Boussole' },
    { value: 'mastermind', label: 'Mastermind' },
    { value: 'path_grid', label: 'Parcours/Grille' },
    { value: 'gears', label: 'Engrenages/Poulies' },
    { value: 'venn', label: 'Diagramme de Venn' },
    { value: 'data_table', label: 'Tableau de données' },
    { value: 'chart', label: 'Graphique (barres, courbe, camembert)' },
    { value: 'figure', label: 'Figure géométrique cotée' },
    { value: 'timeline', label: 'Frise chronologique (fiche)' },
    { value: 'pyramid', label: 'Pyramide / niveaux (fiche)' },
    { value: 'steps', label: 'Étapes de procédure (fiche)' },
    { value: 'bilan', label: 'Bilan à deux colonnes (fiche)' },
];

const RENDERER_DEFAULTS = {
    card_sequence: { cards: ['3♥', '5♦', '7♣', '?'] },
    domino_sequence: { dominos: [[3, 5], [2, 4], ['?', '?']] },
    series: { items: ['A', 'C', 'E', 'G', '?'] },
    matrix: { grid: ['△', '○', '□', '○', '□', '△', '□', '?', '○'], cols: 3 },
    planning: { headers: ['Lun', 'Mar', 'Mer'], rows: [{ label: '9h', cells: ['Marie', 'Luc', '?'] }, { label: '14h', cells: ['Paul', '?', 'Marie'] }] },
    compass: { direction: 'NE' },
    mastermind: { attempts: [{ guess: ['1', '2', '3'], black: 1, white: 1 }, { guess: ['4', '5', '6'], black: 0, white: 1 }] },
    path_grid: { start: [0, 0], moves: ['N 3', 'E 2'] },
    gears: { gears: [{ label: 'A', teeth: 20, direction: 'cw' }, { label: 'B', teeth: 40 }, { label: 'C', teeth: 15 }] },
    venn: { sets: [{ label: 'A' }, { label: 'B' }], zones: { A: '10', B: '7', AB: '?', none: '5' }, total: 30 },
    data_table: { headers: ['Colonne 1', 'Colonne 2'], rows: [['Valeur A', 'Valeur B'], ['Valeur C', 'Valeur D']] },
    figure: { shape: 'rect', title: 'Terrain', w: 8, h: 5, labels: { w: '8 m', h: '5 m' } },
    chart: { kind: 'bar', title: 'Dossiers traités par mois', unit: 'dossiers', labels: ['janv.', 'févr.', 'mars'], series: [{ name: '2026', values: [120, 150, 90] }], source: 'Données fictives' },
    timeline: { title: 'Frise', events: [{ date: '1957', label: 'Traités de Rome', detail: 'CEE et Euratom' }, { date: '1992', label: 'Maastricht' }] },
    pyramid: { title: 'Hiérarchie', levels: [{ label: 'Constitution' }, { label: 'Traités' }, { label: 'Lois' }, { label: 'Règlements' }], note: '' },
    steps: { title: 'Procédure', steps: [{ label: 'Dépôt du projet', actor: 'Gouvernement', delay: '' }, { label: 'Vote', actor: 'Parlement', delay: '70 jours' }] },
    bilan: { title: 'Bilan fonctionnel', left: { title: 'Emplois' }, right: { title: 'Ressources' }, rows: [{ left: 'Emplois stables', right: 'Ressources stables', tone: 'stable' }, { left: 'Actif circulant', right: 'Passif circulant', tone: 'circulant' }, { left: 'Trésorerie active', right: 'Trésorerie passive', tone: 'tresorerie' }], results: [{ label: 'FR', formula: 'Ressources stables − Emplois stables' }] },
};

/**
 * BlockEditor — Rich visual editor for content blocks (enonce, explication, reponses).
 * Renders paragraph and image blocks with inline editing, reorder, soft-delete.
 *
 * Props:
 *   blocks: array of block objects
 *   onChange: (newBlocks) => void
 *   label: string (optional section label)
 *   compact: boolean (optional, for smaller response editors)
 *   fiche: boolean — blocs de fiche de notion en plus (intertitre, liste, tableau, encadré ; migration 187)
 *   nested: boolean — contenu d'un encadré : seulement texte et liste
 */
const BLOCK_META = {
    paragraph: { icon: Type, label: 'Texte' },
    image: { icon: Image, label: 'Image' },
    renderer: { icon: Shapes, label: 'Visuel' },
    heading: { icon: Heading, label: 'Intertitre' },
    list: { icon: List, label: 'Liste' },
    table: { icon: Table2, label: 'Tableau' },
    callout: { icon: MessageSquareQuote, label: 'Encadré' },
};
const RICH_TYPES = ['paragraph', 'list', 'table', 'callout', 'heading'];

export default function BlockEditor({ blocks = [], onChange, label, compact = false, folder = 'questions', showRenderers = false, fiche = false, nested = false }) {

    const handleBlockChange = (index, updates) => {
        const next = blocks.map((b, i) => i === index ? { ...b, ...updates } : b);
        onChange(next);
    };

    const addBlock = (type) => {
        let newBlock;
        if (type === 'paragraph') {
            newBlock = { type: 'paragraph', content: '', isActive: true };
        } else if (type === 'renderer') {
            const defaultRenderer = RENDERER_OPTIONS[0].value;
            newBlock = { type: 'renderer', renderer: defaultRenderer, data: RENDERER_DEFAULTS[defaultRenderer], isActive: true };
        } else if (type === 'heading') {
            newBlock = { type: 'heading', level: 3, content: '', isActive: true };
        } else if (type === 'list') {
            newBlock = { type: 'list', ordered: false, items: [''], isActive: true };
        } else if (type === 'table') {
            newBlock = { type: 'table', headers: ['', ''], rows: [['', '']], isActive: true };
        } else if (type === 'callout') {
            newBlock = { type: 'callout', title: '', tone: 'info', blocks: [{ type: 'paragraph', content: '', isActive: true }], isActive: true };
        } else {
            newBlock = { type: 'image', src: '', alt: '', caption: '', position: 'center', width: 100, isActive: true };
        }
        onChange([...blocks, newBlock]);
    };

    const removeBlock = (index) => {
        // Soft-delete: toggle isActive
        const next = blocks.map((b, i) => i === index ? { ...b, isActive: !b.isActive } : b);
        onChange(next);
    };

    const moveBlock = (index, direction) => {
        const next = [...blocks];
        const target = index + direction;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    const deleteBlock = (index) => {
        onChange(blocks.filter((_, i) => i !== index));
    };

    return (
        <div className={`be ${compact ? 'be--compact' : ''}`}>
            {label && <div className="be-label">{label}</div>}
            <div className="be-blocks">
                {blocks.map((block, index) => (
                    <div key={index} className={`be-block ${!block.isActive ? 'be-block--inactive' : ''}`}>
                        <div className="be-block-toolbar">
                            <span className="be-block-type">
                                {(() => { const M = BLOCK_META[block.type] || BLOCK_META.image; const I = M.icon; return <><I size={12} />{M.label}</>; })()}
                            </span>
                            {RICH_TYPES.includes(block.type) && (
                                <div className="be-block-fmt">
                                    <button type="button" className="be-fmt-btn" onMouseDown={e => e.preventDefault()}
                                        // eslint-disable-next-line no-restricted-syntax
                                        onClick={() => document.execCommand('bold', false, null)} title="Gras (Ctrl+B / Ctrl+G)">
                                        <Bold size={13} />
                                    </button>
                                    <button type="button" className="be-fmt-btn" onMouseDown={e => e.preventDefault()}
                                        // eslint-disable-next-line no-restricted-syntax
                                        onClick={() => document.execCommand('italic', false, null)} title="Italique (Ctrl+I)">
                                        <Italic size={13} />
                                    </button>
                                </div>
                            )}
                            <div className="be-block-actions">
                                <button type="button" className="be-action" onClick={() => moveBlock(index, -1)} disabled={index === 0} title="Monter">
                                    <ArrowUp size={13} />
                                </button>
                                <button type="button" className="be-action" onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1} title="Descendre">
                                    <ArrowDown size={13} />
                                </button>
                                <button type="button" className="be-action" onClick={() => removeBlock(index)} title={block.isActive ? 'Désactiver' : 'Réactiver'}>
                                    {block.isActive ? <EyeOff size={13} /> : <Eye size={13} />}
                                </button>
                                <button type="button" className="be-action be-action--danger" onClick={() => deleteBlock(index)} title="Supprimer définitivement">
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        </div>

                        {block.type === 'paragraph' && (
                            <ParagraphBlock
                                block={block}
                                onChange={(updates) => handleBlockChange(index, updates)}
                                compact={compact}
                            />
                        )}
                        {block.type === 'image' && (
                            <ImageBlock
                                block={block}
                                onChange={(updates) => handleBlockChange(index, updates)}
                                folder={folder}
                            />
                        )}
                        {block.type === 'renderer' && showRenderers && (
                            <RendererBlock
                                block={block}
                                onChange={(updates) => handleBlockChange(index, updates)}
                            />
                        )}
                        {block.type === 'renderer' && !showRenderers && (
                            <QuestionVisual renderer={block.renderer} data={block.data} />
                        )}
                        {block.type === 'heading' && (
                            <div className="be-fiche-row">
                                <select className="be-select" value={block.level || 3} onChange={e => handleBlockChange(index, { level: Number(e.target.value) })}>
                                    <option value={3}>Intertitre</option>
                                    <option value={4}>Sous-intertitre</option>
                                </select>
                                <RichLine value={block.content} onChange={(content) => handleBlockChange(index, { content })} placeholder="Intertitre" />
                            </div>
                        )}
                        {block.type === 'list' && (
                            <ListEditor block={block} onChange={(updates) => handleBlockChange(index, updates)} />
                        )}
                        {block.type === 'table' && (
                            <TableEditor block={block} onChange={(updates) => handleBlockChange(index, updates)} />
                        )}
                        {block.type === 'callout' && (
                            <div className="be-callout">
                                <div className="be-fiche-row">
                                    <RichLine value={block.title} onChange={(title) => handleBlockChange(index, { title })} placeholder="Titre de l'encadré (facultatif)" />
                                    <select className="be-select" value={block.tone || 'info'} onChange={e => handleBlockChange(index, { tone: e.target.value })}>
                                        <option value="info">Encadré</option>
                                        <option value="definition">Définition</option>
                                        <option value="formule">Formule</option>
                                        <option value="methode">Méthode</option>
                                        <option value="exemple">Exemple</option>
                                        <option value="warning">Piège / attention</option>
                                        <option value="retenir">À retenir</option>
                                    </select>
                                </div>
                                <BlockEditor blocks={block.blocks || []} onChange={(inner) => handleBlockChange(index, { blocks: inner })} compact nested folder={folder} />
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div className="be-add-btns">
                <button type="button" className="be-add" onClick={() => addBlock('paragraph')}>
                    <Plus size={13} /> Texte
                </button>
                {(fiche || nested) && (
                    <button type="button" className="be-add" onClick={() => addBlock('list')}>
                        <Plus size={13} /> Liste
                    </button>
                )}
                {fiche && !nested && ['heading', 'table', 'callout'].map(t => (
                    <button key={t} type="button" className="be-add" onClick={() => addBlock(t)}>
                        <Plus size={13} /> {BLOCK_META[t].label}
                    </button>
                ))}
                {!nested && (
                <button type="button" className="be-add" onClick={() => addBlock('image')}>
                    <Plus size={13} /> Image
                </button>
                )}
                {showRenderers && !nested && (
                    <button type="button" className="be-add" onClick={() => addBlock('renderer')}>
                        <Plus size={13} /> Visuel
                    </button>
                )}
            </div>
        </div>
    );
}

function ParagraphBlock({ block, onChange, compact }) {
    const editorRef = useRef(null);
    const lastExternalContent = useRef(block.content || '');

    // Set content on mount AND when block.content changes externally (e.g. AutoImprove apply)
    useEffect(() => {
        if (!editorRef.current) return;
        // Only update if the change came from outside (not from user typing)
        if (block.content !== lastExternalContent.current) {
            lastExternalContent.current = block.content || '';
            if (editorRef.current.innerHTML !== lastExternalContent.current) {
                editorRef.current.innerHTML = lastExternalContent.current;
            }
        }
    }, [block.content]);

    // Also set on initial mount
    useEffect(() => {
        if (editorRef.current && !editorRef.current.innerHTML) {
            editorRef.current.innerHTML = lastExternalContent.current;
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleInput = () => {
        if (editorRef.current) {
            onChange({ content: editorRef.current.innerHTML });
        }
    };

    // Ctrl+G (FR keyboard) → bold. Ctrl+B / Ctrl+I are handled natively.
    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === 'g' || e.key === 'G')) {
            e.preventDefault();
            // eslint-disable-next-line no-restricted-syntax
            document.execCommand('bold', false, null);
            handleInput();
        }
    };

    // Smart paste : ne conserve que <strong> et <em>, strippe tout le reste
    // (couleurs, polices, classes, tailles...). Voir sanitizePaste.js.
    const handlePaste = (e) => {
        handleSmartPaste(e);
        handleInput();
    };

    // The parent BlockEditor injects Bold/Italic buttons in the block toolbar.
    // We need to notify the parent of DOM changes when those buttons are clicked
    // (execCommand mutates the DOM directly without firing 'input').
    useEffect(() => {
        const el = editorRef.current;
        if (!el) return;
        const obs = new MutationObserver(() => {
            onChange({ content: el.innerHTML });
        });
        obs.observe(el, { subtree: true, childList: true, characterData: true, attributes: true });
        return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="be-paragraph">
            <div
                ref={editorRef}
                className={`be-editable ${compact ? 'be-editable--compact' : ''}`}
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                data-placeholder="Saisir le texte ici..."
            />
        </div>
    );
}

function ImageBlock({ block, onChange, folder = 'questions' }) {
    const [uploading, setUploading] = useState(false);
    const [importUrl, setImportUrl] = useState('');
    const [showImport, setShowImport] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    const isExternal = block.src && /^https?:\/\//i.test(block.src);

    // Upload fichier local
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setError('');
        try {
            const form = new FormData();
            form.append('image', file);
            form.append('folder', folder);
            const res = await api.post('/admin/upload.php', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            onChange({ src: res.data.data.url });
        } catch (err) {
            setError(err.response?.data?.error || 'Erreur upload');
        } finally {
            setUploading(false);
        }
    };

    // Importer URL externe en local
    const handleImportUrl = async () => {
        if (!importUrl.trim()) return;
        setUploading(true);
        setError('');
        try {
            const res = await api.post('/admin/upload.php?action=import_url', {
                url: importUrl.trim(),
                folder,
            });
            onChange({ src: res.data.data.url });
            setImportUrl('');
            setShowImport(false);
        } catch (err) {
            setError(err.response?.data?.error || 'Erreur import');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="be-image">
            <div className="be-image-fields">
                <div className="be-field">
                    <label className="be-field-label">
                        Source de l'image
                        {isExternal && <span className="be-badge be-badge--ext">URL externe</span>}
                    </label>
                    <div className="be-image-src-row">
                        <input
                            type="text"
                            className="be-input"
                            value={block.src || ''}
                            onChange={e => onChange({ src: e.target.value })}
                            placeholder="URL ou /uploads/questions/..."
                        />
                        <button
                            type="button"
                            className="be-action-btn"
                            onClick={() => fileInputRef.current?.click()}
                            title="Uploader un fichier"
                            disabled={uploading}
                        >
                            <Upload size={14} /> Fichier
                        </button>
                        <button
                            type="button"
                            className={`be-action-btn ${showImport ? 'be-action-btn--active' : ''}`}
                            onClick={() => setShowImport(v => !v)}
                            title="Importer depuis une URL externe"
                            disabled={uploading}
                        >
                            <Link size={14} /> URL
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="be-hidden-input"
                            onChange={handleFileUpload}
                        />
                    </div>
                    {showImport && (
                        <div className="be-import-url-row">
                            <input
                                type="text"
                                className="be-input"
                                value={importUrl}
                                onChange={e => setImportUrl(e.target.value)}
                                placeholder="https://images.unsplash.com/..."
                                onKeyDown={e => e.key === 'Enter' && handleImportUrl()}
                            />
                            <button
                                type="button"
                                className="be-action-btn be-action-btn--primary"
                                onClick={handleImportUrl}
                                disabled={uploading || !importUrl.trim()}
                                title="Télécharger et sauvegarder en local"
                            >
                                <Download size={14} /> {uploading ? 'Import...' : 'Importer en local'}
                            </button>
                        </div>
                    )}
                    {error && <div className="be-error">{error}</div>}
                </div>
                <div className="be-field-row">
                    <div className="be-field be-field--grow">
                        <label className="be-field-label">Texte alternatif</label>
                        <input
                            type="text"
                            className="be-input"
                            value={block.alt || ''}
                            onChange={e => onChange({ alt: e.target.value })}
                            placeholder="Description de l'image"
                        />
                    </div>
                    <div className="be-field">
                        <label className="be-field-label">Position</label>
                        <select className="be-select" value={block.position || 'center'} onChange={e => onChange({ position: e.target.value })}>
                            <option value="left">Gauche</option>
                            <option value="center">Centre</option>
                            <option value="right">Droite</option>
                        </select>
                    </div>
                    <div className="be-field">
                        <label className="be-field-label">Largeur {block.width || 100}%</label>
                        <input
                            type="range"
                            min="10"
                            max="100"
                            step="5"
                            value={block.width || 100}
                            onChange={e => onChange({ width: parseInt(e.target.value) })}
                            className="be-range"
                        />
                    </div>
                </div>
                <div className="be-field">
                    <label className="be-field-label">Légende</label>
                    <input
                        type="text"
                        className="be-input"
                        value={block.caption || ''}
                        onChange={e => onChange({ caption: e.target.value })}
                        placeholder="Légende optionnelle"
                    />
                </div>
            </div>
            {block.src && (
                <div className="be-image-preview" style={{ textAlign: block.position || 'center' }}>
                    <img src={block.src.startsWith('uploads/') ? `/${block.src}` : block.src} alt={block.alt || ''} style={{ maxWidth: `${block.width || 100}%` }} />
                    {block.caption && <figcaption className="be-image-caption">{block.caption}</figcaption>}
                </div>
            )}
        </div>
    );
}

function RendererBlock({ block, onChange }) {
    const [dataStr, setDataStr] = useState(() =>
        block.data ? JSON.stringify(block.data, null, 2) : ''
    );

    const handleRendererChange = (renderer) => {
        const defaults = RENDERER_DEFAULTS[renderer] || {};
        const str = JSON.stringify(defaults, null, 2);
        setDataStr(str);
        onChange({ renderer, data: defaults });
    };

    const handleDataChange = (str) => {
        setDataStr(str);
        try { onChange({ data: JSON.parse(str) }); } catch {}
    };

    return (
        <div className="be-renderer">
            <div className="qv-editor">
                <div className="qv-editor__row">
                    <label>Type :</label>
                    <select
                        value={block.renderer || ''}
                        onChange={e => handleRendererChange(e.target.value)}
                    >
                        {RENDERER_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
                <div className="qv-editor__row">
                    <label>Data :</label>
                    <textarea
                        value={dataStr}
                        onChange={e => handleDataChange(e.target.value)}
                        rows={4}
                        spellCheck={false}
                        className="qv-editor__textarea"
                    />
                </div>
                <div className="qv-editor__preview">
                    <div className="qv-editor__preview-label">Aperçu</div>
                    <QuestionVisual renderer={block.renderer} data={block.data} />
                </div>
            </div>
        </div>
    );
}

/** Champ riche d'une ligne (gras, italique) : éléments de liste, cellules, titres de fiche. */
function RichLine({ value, onChange, placeholder }) {
    const ref = useRef(null);
    const last = useRef(value || '');
    useEffect(() => {
        if (ref.current && ref.current.innerHTML !== (value || '') && value !== last.current) {
            last.current = value || '';
            ref.current.innerHTML = last.current;
        }
    }, [value]);
    useEffect(() => { if (ref.current) ref.current.innerHTML = last.current; }, []);
    const emit = () => { if (ref.current) { last.current = ref.current.innerHTML; onChange(ref.current.innerHTML); } };
    return (
        <div ref={ref} className="be-editable be-editable--compact be-richline" contentEditable suppressContentEditableWarning
            onInput={emit} onBlur={emit} data-placeholder={placeholder}
            onPaste={(e) => { handleSmartPaste(e); emit(); }}
            onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && (e.key === 'g' || e.key === 'G')) {
                    e.preventDefault();
                    // eslint-disable-next-line no-restricted-syntax
                    document.execCommand('bold', false, null); emit();
                }
            }} />
    );
}

function ListEditor({ block, onChange }) {
    const items = block.items || [];
    const setItem = (i, v) => onChange({ items: items.map((x, k) => k === i ? v : x) });
    return (
        <div className="be-list">
            <label className="be-fiche-check"><input type="checkbox" checked={!!block.ordered} onChange={e => onChange({ ordered: e.target.checked })} /> Liste numérotée</label>
            {items.map((it, i) => (
                <div key={i} className="be-fiche-row">
                    <span className="be-list-mark">{block.ordered ? `${i + 1}.` : '•'}</span>
                    <RichLine value={it} onChange={(v) => setItem(i, v)} placeholder="Élément" />
                    <button type="button" className="be-action" title="Monter" disabled={i === 0}
                        onClick={() => { const n = [...items]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; onChange({ items: n }); }}><ArrowUp size={13} /></button>
                    <button type="button" className="be-action be-action--danger" title="Retirer" onClick={() => onChange({ items: items.filter((_, k) => k !== i) })}><X size={13} /></button>
                </div>
            ))}
            <button type="button" className="be-add" onClick={() => onChange({ items: [...items, ''] })}><Plus size={13} /> Élément</button>
        </div>
    );
}

function TableEditor({ block, onChange }) {
    const headers = block.headers || [];
    const rows = block.rows || [];
    const cols = Math.max(headers.length, ...rows.map(r => r.length), 1);
    const pad = (r) => [...r, ...Array(Math.max(0, cols - r.length)).fill('')];
    const setHeader = (j, v) => onChange({ headers: pad(headers).map((x, k) => k === j ? v : x) });
    const setCell = (i, j, v) => onChange({ rows: rows.map((r, k) => k === i ? pad(r).map((x, l) => l === j ? v : x) : r) });
    return (
        <div className="be-table">
            <div className="be-table-grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(120px, 1fr)) 28px` }}>
                {pad(headers).map((h, j) => <div key={`h${j}`} className="be-table-head"><RichLine value={h} onChange={(v) => setHeader(j, v)} placeholder={`En-tête ${j + 1}`} /></div>)}
                <button type="button" className="be-action be-action--danger" title="Retirer la dernière colonne" disabled={cols <= 1}
                    onClick={() => onChange({ headers: pad(headers).slice(0, cols - 1), rows: rows.map(r => pad(r).slice(0, cols - 1)) })}><X size={13} /></button>
                {rows.map((r, i) => (
                    <div key={`r${i}`} style={{ display: 'contents' }}>
                        {pad(r).map((c, j) => <div key={j}><RichLine value={c} onChange={(v) => setCell(i, j, v)} placeholder="…" /></div>)}
                        <button type="button" className="be-action be-action--danger" title="Retirer la ligne" onClick={() => onChange({ rows: rows.filter((_, k) => k !== i) })}><X size={13} /></button>
                    </div>
                ))}
            </div>
            <div className="be-fiche-row">
                <button type="button" className="be-add" onClick={() => onChange({ rows: [...rows, Array(cols).fill('')] })}><Plus size={13} /> Ligne</button>
                <button type="button" className="be-add" onClick={() => onChange({ headers: [...pad(headers), ''], rows: rows.map(r => [...pad(r), '']) })}><Plus size={13} /> Colonne</button>
            </div>
        </div>
    );
}
